'use client';

import { useEffect, useRef, useState } from 'react';
import { deleteCurrentUser } from '@/services/firebase-admin/delete-current-user';
import { wipeCustomClaims } from '@/services/firebase-admin/wipe-custom-claims';
import { auth } from '@/utils/firebase-client';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
  User,
} from 'firebase/auth';
import { BookingUser } from 'types/auth';

export function SessionDebugTray() {
  const [user, setUser] = useState<BookingUser | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const refreshClaims = async (firebaseUser: User) => {
      // Prevent concurrent refresh calls
      if (isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;
      try {
        // Use false to avoid forcing a refresh (just get current result)
        const tokenResult = await firebaseUser.getIdTokenResult(false);
        setClaims(tokenResult.claims);
      } catch (error) {
        console.error('Error fetching token claims:', error);
        setClaims(null);
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Listen for auth state changes (login/logout)
    const unsubscribeAuth = onAuthStateChanged(
      auth,
      async (firebaseUser: User | null) => {
        if (!firebaseUser) {
          setUser(null);
          setClaims(null);
          return;
        }
        const signedInUser: BookingUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email as string,
          displayName: firebaseUser.displayName as string,
          phone: firebaseUser.phoneNumber,
          providers: firebaseUser.providerData,
        };
        setUser(signedInUser);

        // Fetch JWT claims
        await refreshClaims(firebaseUser);
      }
    );

    // Listen for token changes (including automatic refreshes)
    // Use a small delay to debounce rapid token changes
    let tokenRefreshTimeout: NodeJS.Timeout | null = null;
    const unsubscribeToken = onIdTokenChanged(
      auth,
      async (firebaseUser: User | null) => {
        if (firebaseUser) {
          // Debounce token refresh updates to prevent infinite loops
          if (tokenRefreshTimeout) {
            clearTimeout(tokenRefreshTimeout);
          }
          tokenRefreshTimeout = setTimeout(async () => {
            // Token was refreshed, update claims
            await refreshClaims(firebaseUser);
          }, 100);
        }
      }
    );

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
      if (tokenRefreshTimeout) {
        clearTimeout(tokenRefreshTimeout);
      }
    };
  }, []);

  // Check if we should show the debug tray
  // Requires BOTH: development environment AND supported hostname
  const shouldShowDebugTray = () => {
    // Must be in development environment
    if (process.env.NEXT_PUBLIC_ENV !== 'development') {
      return false;
    }

    // Must also be on a supported hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const allowedHostnames = [
        'localhost',
        '127.0.0.1',
        'mrr-local.site',
        'bvr-local.site',
        'booking-platform-pr1202-716213441189.us-west1.run.app',
      ];

      return allowedHostnames.includes(hostname);
    }

    return false;
  };

  // Only render in development AND on allowed URLs
  if (!shouldShowDebugTray()) {
    return null;
  }

  const formatClaims = (claims: Record<string, unknown> | null): string => {
    if (!claims) return '';
    // Standard Firebase JWT claims to filter out
    const standardClaims = [
      'iss', // issuer
      'aud', // audience
      'auth_time', // authentication time
      'iat', // issued at
      'exp', // expiration
      'firebase', // Firebase-specific nested object
      'sub', // subject/user ID
      'email', // user email
      'email_verified', // email verification status
      'name', // user display name
      'picture', // user profile picture
      'phone_number', // user phone number
      'phone_number_verified', // phone verification status
      'user_id', // user ID (sometimes present)
    ];
    // Filter out standard Firebase claims and format only custom claims
    const customClaims = Object.entries(claims)
      .filter(([key]) => !standardClaims.includes(key))
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');

    const claimsStr = customClaims ? `Claims: {${customClaims}}` : 'Claims: {}';

    // Check if account_id claim is missing (legacy user)
    const hasAccountId =
      claims.account_id !== undefined && claims.account_id !== null;
    const legacySuffix = !hasAccountId ? ' (LEGACY)' : '';

    return `${claimsStr}${legacySuffix}`;
  };

  const sessionStatus = user
    ? [
        'Session: Active',
        `Phone: ${user.phone || 'n/a'}`,
        `Email: ${user.email || 'n/a'}`,
        formatClaims(claims),
      ]
        .filter(Boolean)
        .join('  |  ')
    : 'No Session';

  const handleLogout = async () => {
    await signOut(auth)
      .then(() => {
        console.log('Signed out.');
      })
      .catch((error) => {
        console.error('Sign out error', error);
      });
  };

  const handleDeleteCurrentUser = async () => {
    if (!user) return;

    if (
      !confirm(
        `Delete current user account (${user.email})? This will logout and permanently delete your Firebase and Firestore records.`
      )
    ) {
      return;
    }

    const uid = user.uid;

    try {
      await signOut(auth);
      console.log('Logged out before deleting user');

      const result = await deleteCurrentUser(uid);
      if (result.success) {
        alert(`Success: ${result.message}`);
      } else {
        alert(`Failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error deleting current user:', error);
      alert('Error deleting account');
    }
  };

  const handleWipeClaims = async () => {
    if (!user) return;

    if (
      !confirm(
        `Wipe all custom claims for current user (${user.email})? This will remove account_id and any other custom claims.`
      )
    ) {
      return;
    }

    const uid = user.uid;

    try {
      const result = await wipeCustomClaims(uid);
      if (result.success) {
        alert(
          `Success: ${result.message}\n\nNote: You may need to refresh your token to see the changes.`
        );
        // Force token refresh to pick up the cleared claims
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(true);
          // Refresh claims in the UI
          const tokenResult = await currentUser.getIdTokenResult();
          setClaims(tokenResult.claims);
        }
      } else {
        alert(`Failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error wiping custom claims:', error);
      alert('Error wiping claims');
    }
  };

  const handleRefreshToken = async () => {
    if (!user) return;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert('No authenticated user found');
        return;
      }

      // Force refresh the token (true = force refresh)
      await currentUser.getIdToken(true);

      // Get the updated token result with fresh claims
      const tokenResult = await currentUser.getIdTokenResult();
      setClaims(tokenResult.claims);

      console.log('Token refreshed, new claims:', tokenResult.claims);
      alert('Token refreshed successfully! Claims updated in debug tray.');
    } catch (error) {
      console.error('Error refreshing token:', error);
      alert(
        `Error refreshing token: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '10vh',
        backgroundColor: '#FFEB3B', // Bright yellow
        color: '#000000',
        opacity: 0.9,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '10px 20px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ fontSize: '14px', fontWeight: '500' }}>{sessionStatus}</div>
      <div
        style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
        }}
      >
        {user && (
          <>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                backgroundColor: '#000000',
                color: '#FFEB3B',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
              }}
            >
              Logout
            </button>
            <button
              onClick={handleWipeClaims}
              style={{
                padding: '8px 16px',
                backgroundColor: '#000000',
                color: '#FFEB3B',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
              }}
            >
              Wipe Claims
            </button>
            <button
              onClick={handleRefreshToken}
              style={{
                padding: '8px 16px',
                backgroundColor: '#000000',
                color: '#FFEB3B',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
              }}
            >
              Refresh Token
            </button>
            <button
              onClick={handleDeleteCurrentUser}
              style={{
                padding: '8px 16px',
                backgroundColor: '#D32F2F',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B71C1C';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D32F2F';
              }}
            >
              Delete Current User
            </button>
          </>
        )}
      </div>
    </div>
  );
}
